import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import apiClient from '../../utils/apiClient';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Card } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { toast } from 'sonner';
import { Store, Search, Filter, CheckCircle, ShoppingBag, Star } from 'lucide-react';
import { Skeleton } from '../../components/ui/skeleton';
import SkillFeedbackStats from '../../components/SkillFeedbackStats';

export default function Marketplace() {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const isRTL = i18n.language === 'ar';
  const [skills, setSkills] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [categories, setCategories] = useState([]);

  // Helper to get translated category label
  const getCategoryLabel = (category) => {
    if (category === 'all') return t('marketplace.allCategories');
    // Try to get from categories namespace, fallback to original
    const translated = t(`categories.${category}`, { defaultValue: '' });
    return translated || category;
  };

  useEffect(() => {
    fetchMarketplaceSkills();
    fetchCategories();
  }, [selectedCategory]);

  const fetchMarketplaceSkills = async () => {
    try {
      setLoading(true);
      const params = {};
      if (selectedCategory !== 'all') {
        params.category = selectedCategory;
      }
      if (searchTerm) {
        params.search = searchTerm;
      }
      
      const response = await apiClient.get('/marketplace/skills', { params });
      setSkills(response.data);
    } catch (error) {
      console.error('Failed to fetch marketplace skills:', error);
      toast.error(t('errors.loadFailed'));
    } finally {
      setLoading(false);
    }
  };

  const fetchCategories = async () => {
    try {
      const response = await apiClient.get('/marketplace/categories');
      setCategories(['all', ...response.data.categories]);
    } catch (error) {
      console.error('Failed to fetch categories:', error);
      // Silently fail for categories as they're non-critical
    }
  };

  const handleGetSkill = async (skillPackageId) => {
    try {
      await apiClient.post(`/marketplace/skills/${skillPackageId}/claim`);
      toast.success(t('success.addedToLibrary') || 'Added to library successfully!');
      fetchMarketplaceSkills();
    } catch (error) {
      console.error('Failed to get skill:', error);
      toast.error(error.response?.data?.detail || t('errors.saveFailed'));
    }
  };

  const handleInstallSkill = async (skillPackageId) => {
    try {
      await apiClient.post('/skills/install', {
        skillPackageId: skillPackageId,
        twinId: 'default-twin',
        approvalMode: 'assisted',
        allowedDataSources: {
          vault: true,
          textTraining: true
        }
      });
      toast.success('Skill installed successfully! You can now use it in the AI Assistant.');
      fetchMarketplaceSkills();
    } catch (error) {
      console.error('Failed to install skill:', error);
      toast.error(error.response?.data?.detail || 'Failed to install skill');
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    fetchMarketplaceSkills();
  };

  const getRequirementBadges = (requirements) => {
    const badges = [];
    if (requirements.text) badges.push({ label: t('marketplace.text'), color: 'bg-blue-100 text-blue-700' });
    if (requirements.voice) badges.push({ label: t('marketplace.voice'), color: 'bg-purple-100 text-purple-700' });
    if (requirements.video) badges.push({ label: t('marketplace.video'), color: 'bg-pink-100 text-pink-700' });
    return badges;
  };

  const getRiskBadgeColor = (risk) => {
    switch (risk) {
      case 'low': return 'bg-green-100 text-green-700';
      case 'medium': return 'bg-yellow-100 text-yellow-700';
      case 'high': return 'bg-red-100 text-red-700';
      default: return 'bg-gray-100 text-gray-700';
    }
  };

  const getRiskLabel = (risk) => {
    switch (risk) {
      case 'low': return t('marketplace.riskLow');
      case 'medium': return t('marketplace.riskMedium');
      case 'high': return t('marketplace.riskHigh');
      default: return risk;
    }
  };

  if (loading) {
    return (
      <div className="max-w-7xl mx-auto p-4 md:p-6 space-y-4 md:space-y-6">
        {/* Header Skeleton */}
        <div className="space-y-2">
          <Skeleton className="h-10 w-64" />
          <Skeleton className="h-5 w-96" />
        </div>

        {/* Search Bar Skeleton */}
        <div className="flex gap-3">
          <Skeleton className="h-10 flex-1" />
          <Skeleton className="h-10 w-32" />
        </div>

        {/* Categories Skeleton */}
        <div className="flex gap-2 overflow-x-auto pb-2">
          {[1, 2, 3, 4, 5].map((i) => (
            <Skeleton key={i} className="h-9 w-24 flex-shrink-0" />
          ))}
        </div>

        {/* Skills Grid Skeleton */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {[1, 2, 3, 4, 5, 6].map((i) => (
            <Card key={i} className="p-6 space-y-4">
              <div className="flex items-start justify-between">
                <Skeleton className="h-6 w-40" />
                <Skeleton className="h-5 w-16" />
              </div>
              <Skeleton className="h-4 w-full" />
              <Skeleton className="h-4 w-3/4" />
              <div className="flex gap-2">
                <Skeleton className="h-5 w-16" />
                <Skeleton className="h-5 w-16" />
              </div>
              <Skeleton className="h-10 w-full" />
            </Card>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto p-6 space-y-6" data-testid="marketplace-page" dir={isRTL ? 'rtl' : 'ltr'}>
      {/* Header */}
      <div className="space-y-2">
        <div className={`flex items-center gap-3 ${isRTL ? 'flex-row-reverse' : ''}`}>
          <Store className="w-8 h-8 text-primary" />
          <h1 className="text-3xl font-bold text-gray-900">{t('marketplace.title')}</h1>
        </div>
        <p className={`text-gray-600 ${isRTL ? 'text-right' : ''}`}>
          {t('marketplace.subtitle')}
        </p>
      </div>

      {/* Search and Filter */}
      <div className={`flex flex-col md:flex-row gap-4 ${isRTL ? 'md:flex-row-reverse' : ''}`}>
        <form onSubmit={handleSearch} className={`flex-1 flex gap-2 ${isRTL ? 'flex-row-reverse' : ''}`}>
          <div className="relative flex-1">
            <Search className={`absolute ${isRTL ? 'right-3' : 'left-3'} top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5`} />
            <Input
              type="text"
              placeholder={t('marketplace.searchPlaceholder')}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className={isRTL ? 'pr-10 text-right' : 'pl-10'}
              data-testid="marketplace-search"
            />
          </div>
          <Button type="submit" data-testid="search-button">
            {t('common.search')}
          </Button>
        </form>
      </div>

      {/* Category Filter */}
      <div className={`flex items-center gap-2 overflow-x-auto pb-2 ${isRTL ? 'flex-row-reverse' : ''}`}>
        <Filter className="w-5 h-5 text-gray-500 flex-shrink-0" />
        <div className={`flex gap-2 ${isRTL ? 'flex-row-reverse' : ''}`}>
          {categories.map((category) => (
            <Button
              key={category}
              variant={selectedCategory === category ? 'default' : 'outline'}
              size="sm"
              onClick={() => setSelectedCategory(category)}
              className="whitespace-nowrap"
              data-testid={`category-${category}`}
            >
              {getCategoryLabel(category)}
            </Button>
          ))}
        </div>
      </div>

      {/* Skills Grid */}
      {skills.length === 0 ? (
        <Card className="p-12 text-center">
          <Store className="w-16 h-16 text-gray-300 mx-auto mb-4" />
          <h3 className="text-xl font-semibold text-gray-900 mb-2">{t('marketplace.noSkills')}</h3>
          <p className="text-gray-600">
            {searchTerm || selectedCategory !== 'all' 
              ? t('marketplace.tryDifferentSearch')
              : t('marketplace.noSkills')}
          </p>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {skills.map((skill) => (
            <Card 
              key={skill.id} 
              className="p-6 hover:shadow-lg transition-shadow cursor-pointer"
              onClick={() => navigate(`/personal/marketplace/${skill.skillPackageId}`)}
              data-testid={`skill-card-${skill.skillPackageId}`}
            >
              {/* Header */}
              <div className="space-y-3 mb-4">
                <div className={`flex items-start justify-between ${isRTL ? 'flex-row-reverse' : ''}`}>
                  <h3 className={`text-lg font-semibold text-gray-900 line-clamp-1 ${isRTL ? 'text-right' : ''}`}>
                    {skill.name}
                  </h3>
                  {skill.isOwned && (
                    <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0" />
                  )}
                </div>
                
                <div className={`flex items-center gap-2 ${isRTL ? 'flex-row-reverse' : ''}`}>
                  <Badge variant="outline" className="text-xs">
                    {skill.publisherType === 'platform' ? `🏢 ${t('marketplace.platform')}` : `👤 ${t('marketplace.community')}`}
                  </Badge>
                  <Badge className={getRiskBadgeColor(skill.riskLevel) + " text-xs"}>
                    {getRiskLabel(skill.riskLevel)}
                  </Badge>
                </div>
              </div>

              {/* Description */}
              <p className={`text-sm text-gray-600 line-clamp-3 mb-4 ${isRTL ? 'text-right' : ''}`}>
                {skill.description}
              </p>

              {/* Requirements */}
              <div className={`flex flex-wrap gap-2 mb-4 ${isRTL ? 'flex-row-reverse' : ''}`}>
                {getRequirementBadges(skill.requirements).map((badge, idx) => (
                  <Badge key={idx} className={badge.color + " text-xs"}>
                    {badge.label}
                  </Badge>
                ))}
              </div>

              {/* Feedback Stats */}
              <div className="mb-3">
                <SkillFeedbackStats skillPackageId={skill.skillPackageId} skillType="marketplace" compact />
              </div>

              {/* Footer */}
              <div className={`flex items-center justify-between pt-4 border-t ${isRTL ? 'flex-row-reverse' : ''}`}>
                <div className={`flex items-center gap-4 text-sm text-gray-600 ${isRTL ? 'flex-row-reverse' : ''}`}>
                  {skill.installCount > 0 && (
                    <span className={`flex items-center gap-1 ${isRTL ? 'flex-row-reverse' : ''}`}>
                      <ShoppingBag className="w-4 h-4" />
                      {skill.installCount}
                    </span>
                  )}
                  {skill.rating && (
                    <span className={`flex items-center gap-1 ${isRTL ? 'flex-row-reverse' : ''}`}>
                      <Star className="w-4 h-4 fill-yellow-400 text-yellow-400" />
                      {skill.rating.toFixed(1)}
                    </span>
                  )}
                  {skill.priceModel === 'free' && (
                    <Badge variant="secondary" className="bg-green-50 text-green-700 text-xs">
                      {t('marketplace.pilot')}
                    </Badge>
                  )}
                </div>
                
                <Button
                  size="sm"
                  variant={skill.isInstalled ? 'outline' : (skill.isOwned ? 'default' : 'default')}
                  onClick={(e) => {
                    e.stopPropagation();
                    if (skill.isInstalled) {
                      navigate(`/personal/skills`);
                    } else if (skill.isOwned) {
                      // Install directly without navigating to detail page
                      handleInstallSkill(skill.skillPackageId);
                    } else {
                      handleGetSkill(skill.skillPackageId);
                    }
                  }}
                  data-testid={`action-skill-${skill.skillPackageId}`}
                >
                  {skill.isInstalled ? t('mySkills.configure') : (skill.isOwned ? t('common.install') : t('common.get'))}
                </Button>
              </div>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
